import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const useSolidHeader = isScrolled || location.pathname === '/half-marathon';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsScrolled(window.scrollY > 20);
  }, [location.pathname, location.hash]);

  const navItems = [
    {
      label: 'Races',
      items: [
        { label: 'Half Marathon (21.1KM)', href: '/half-marathon' },
        { label: '10km Run', href: '/#races' },
        { label: '5km Run', href: '/#races' },
        { label: '2km Fun Run', href: '/#races' },
      ],
    },
    {
      label: 'Event info',
      items: [
        { label: 'FAQ', href: '/faq' },
        { label: 'Event Schedule', href: '/event-info' },
        { label: 'Registration Times', href: '/event-info#event-info' },
        { label: 'Race Day Briefing', href: '/event-info#event-info' },
      ],
    },
    {
      label: 'Discover Camden',
      items: [
        { label: 'Local Landmarks', href: '/#features' },
        { label: 'Country Charm', href: '/#features' },
      ],
    },
    {
      label: 'Sponsors',
      items: [
        { label: 'Current Partners', href: '/#sponsors' },
        { label: 'Become a Sponsor', href: '/#sponsors' },
        { label: 'Sponsorship Enquiry', href: '/#sponsors' },
      ],
    },
    {
      label: 'Event Team',
      items: [
        { label: 'Meet the Team', href: '#' },
        { label: 'Volunteer Roles', href: '#' },
        { label: 'Contact Organisers', href: '#' },
      ],
    },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        useSolidHeader
          ? 'bg-background/95 backdrop-blur-md border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <nav className="container-wide pt-2 md:pt-3">
        <div className="flex items-center justify-between min-h-20 gap-3">
          {/* Logo - Responsive sizing for mobile */}
          <a href="/" className="flex items-center min-w-0 flex-1 max-w-[260px] sm:max-w-[290px] md:max-w-[320px] -ml-20 sm:-ml-16 md:-ml-32">
            <img 
              src="/run-camden-logo.png" 
              alt="Run Camden Logo" 
              className="block w-full max-h-16 sm:max-h-[68px] md:max-h-[72px] h-auto object-contain origin-left scale-125"
            />
          </a>

          {/* Desktop Navigation - WHITE STROKE WITH GREEN HOVER */}
          <div className="hidden lg:flex items-center gap-2">
            {navItems.map((item) => (
              <DropdownMenu key={item.label}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`px-5 py-2 text-sm font-bold uppercase tracking-wider transition-all duration-300 rounded inline-flex items-center gap-2 ${
                      useSolidHeader
                        ? 'text-foreground hover:bg-primary hover:text-foreground'
                        : 'border-2 border-white text-white hover:bg-primary hover:text-foreground hover:border-primary'
                    }`}
                    aria-label={`${item.label} menu`}
                  >
                    {item.label}
                    <ChevronDown size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="start">
                  <DropdownMenuLabel>{item.label}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {item.items.map((subItem) => {
                    const isExternal = subItem.href.startsWith('http');
                    return (
                      <DropdownMenuItem
                        key={`${item.label}-${subItem.label}`}
                        className="focus:bg-primary focus:text-foreground"
                        asChild
                      >
                        <a
                          href={subItem.href}
                          target={isExternal ? '_blank' : undefined}
                          rel={isExternal ? 'noreferrer' : undefined}
                        >
                          {subItem.label}
                        </a>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            <div className="ml-4">
              {/* BOLD ATHLETIC HEADER CTA */}
              <Button className="btn-athletic-header">
                Register Now
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button - WITH WHITE STROKE BOX */}
          <button
            className={`lg:hidden p-2 rounded flex-shrink-0 transition-all duration-300 ${
              useSolidHeader ? '' : 'border-2 border-white'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={useSolidHeader ? 'text-foreground' : 'text-white'} size={28} />
            ) : (
              <Menu className={useSolidHeader ? 'text-foreground' : 'text-white'} size={28} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border">
            <div className="py-6 space-y-1">
              {navItems.map((item) => (
                <details
                  key={item.label}
                  className="border-b border-border last:border-b-0"
                >
                  <summary className="px-6 py-4 text-base font-bold uppercase tracking-wider text-foreground hover:bg-muted transition-colors cursor-pointer list-none flex items-center justify-between">
                    <span>{item.label}</span>
                    <ChevronDown size={18} />
                  </summary>
                  <div className="pb-3">
                    {item.items.map((subItem) => {
                      const isExternal = subItem.href.startsWith('http');
                      return (
                        <a
                          key={`${item.label}-${subItem.label}`}
                          href={subItem.href}
                          className="block px-10 py-2 text-sm font-semibold tracking-wide text-muted-foreground hover:text-foreground"
                          target={isExternal ? '_blank' : undefined}
                          rel={isExternal ? 'noreferrer' : undefined}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.label}
                        </a>
                      );
                    })}
                  </div>
                </details>
              ))}
              <div className="px-6 pt-6">
                <Button className="btn-athletic-header w-full">
                  Register Now
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
